import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";

const LocationTracker = () => {
  /*
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [logged, setLogged] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [sentOnce, setSentOnce] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!Cookies.get("token"));

  // ---- Geolocation caching & platform detection ----
  // Cache keys (scoped to this app)
  const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
  const GEO_ALLOWED_KEY = "pb_geo_allowed";
  const GEO_TS_KEY = "pb_geo_ts";
  const GEO_LAT_KEY = "pb_geo_lat";
  const GEO_LNG_KEY = "pb_geo_lng";
  const GEO_SUPPRESS_KEY = "pb_geo_suppress_until"; // suppress re-prompt UI until timestamp

  // Basic iOS detection (covers Safari and most in-app browsers)
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document);

  // Read cached geolocation info from localStorage
  const readCache = () => {
    const allowed = localStorage.getItem(GEO_ALLOWED_KEY) === "true";
    const ts = parseInt(localStorage.getItem(GEO_TS_KEY) || "0", 10);
    const lat = parseFloat(localStorage.getItem(GEO_LAT_KEY) || "");
    const lng = parseFloat(localStorage.getItem(GEO_LNG_KEY) || "");
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    return { allowed, ts, lat, lng, hasCoords };
  };

  // Write latest coordinates and timestamp to cache
  const writeCache = (lat, lng) => {
    localStorage.setItem(GEO_LAT_KEY, String(lat));
    localStorage.setItem(GEO_LNG_KEY, String(lng));
    localStorage.setItem(GEO_TS_KEY, String(Date.now()));
  };

  // Mark whether user has granted geolocation permission in this app
  const markAllowed = (value) => {
    localStorage.setItem(GEO_ALLOWED_KEY, value ? "true" : "false");
  };

  // Suppress permission UI until a specific time to avoid repeated popups
  const suppressPrompts = (ms) => {
    localStorage.setItem(GEO_SUPPRESS_KEY, String(Date.now() + ms));
  };
  const isSuppressed = () => {
    const until = parseInt(localStorage.getItem(GEO_SUPPRESS_KEY) || "0", 10);
    return Date.now() < until;
  };

  const logCoords = (latitude, longitude) => {
    // Persist once per session to backend; throttled by sentOnce in this component
    console.log(latitude, longitude);
    if (!logged) setLogged(true);
    if (!sentOnce) {
      sendLocationToBackend(latitude, longitude);
      setSentOnce(true);
    }
  };

  // Initial load: decide whether to prompt or use cached data
  useEffect(() => {
    if (!isLoggedIn) {
      setOpen(false);
      return;
    }
    const cache = readCache();
    const fresh = cache.ts && Date.now() - cache.ts < REFRESH_INTERVAL_MS;

    // If we have fresh coordinates and permission marked allowed, reuse silently
    if (cache.allowed && fresh) {
      setOpen(false);
      if (!sentOnce && cache.hasCoords) {
        logCoords(cache.lat, cache.lng);
      }
      return;
    }

    // Auto-refresh location if previously allowed (try silent refresh on all platforms)
    if (cache.allowed && !fresh) {
      setOpen(false);
      requestLocation();
      return;
    }

    // Otherwise show the prompt only if not allowed (unless suppressed)
    if (!cache.allowed) {
      setOpen(!isSuppressed());
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setOpen(false);
      return;
    }
    const cache = readCache();
    const fresh = cache.ts && Date.now() - cache.ts < REFRESH_INTERVAL_MS;

    // On route change, do not re-request if within time window
    if (cache.allowed && fresh) {
      setOpen(false);
      if (!sentOnce && cache.hasCoords) {
        logCoords(cache.lat, cache.lng);
      }
      return;
    }

    // If allowed but stale, try to refresh silently first
    if (cache.allowed) {
      setOpen(false);
      requestLocation();
      return;
    }

    // Double-check real browser permission status if we think it's not allowed
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          markAllowed(true);
          setOpen(false);
          requestLocation();
        } else {
          setOpen(!isSuppressed());
        }
      });
      return;
    }

    // If NOT allowed, show the prompt (unless suppressed)
    setOpen(!isSuppressed());
  }, [location, isLoggedIn]);

  const requestLocation = () => {
    if (!isLoggedIn) return;
    if (!("geolocation" in navigator)) {
      setError("Location not supported on this device");
      return;
    }

    setLoading(true);
    setError("");

    let settled = false;

    const onSuccess = (pos) => {
      if (settled) return;
      settled = true;
      const { latitude, longitude } = pos.coords;
      // Cache the latest position and mark permission allowed
      writeCache(latitude, longitude);
      markAllowed(true);
      logCoords(latitude, longitude);
      setLoading(false);
      setOpen(false);
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        setWatchId(null);
      }
    };

    const onError = (err) => {
      if (settled) return;
      if (err && err.code === err.PERMISSION_DENIED) {
        // User denied; remember to avoid repeated prompts
        markAllowed(false);
        suppressPrompts(2 * 60 * 1000); // suppress for 2 minutes
        setLoading(false);
        setError("Permission denied. Please enable location in browser settings.");
        setOpen(true);
        return;
      }
      tryLowAccuracy(onSuccess, () => startWatch(onSuccess, afterWatchError));
    };

    const afterWatchError = () => {
      // Don't use IP fallback. We strictly want GPS permission.
      // ipFallback(); 
      setLoading(false);
      setError("Location retrieval failed. Please allow location access.");
      setOpen(!isSuppressed());
      
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        setWatchId(null);
      }
    };

    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 15000,
      // Allow device/location provider cache for up to the refresh interval
      maximumAge: REFRESH_INTERVAL_MS,
    });
  };

  const tryLowAccuracy = (onSuccess, onFailure) => {
    try {
      navigator.geolocation.getCurrentPosition(
        onSuccess,
        () => onFailure(),
        { enableHighAccuracy: false, timeout: 20000, maximumAge: REFRESH_INTERVAL_MS }
      );
    } catch {
      onFailure();
    }
  };

  const sendLocationToBackend = async (lat, lng) => {
    try {
      const token = Cookies.get("token");
      if (!token) return;
      await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/api/user/update-location",
        { lat, lng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // silent fail for UX
    }
  };

  const startWatch = (onSuccess, onFailure) => {
    try {
      const id = navigator.geolocation.watchPosition(
        (pos) => {
          onSuccess(pos);
        },
        () => {
          onFailure();
        },
        {
          enableHighAccuracy: false,
          timeout: 30000,
          maximumAge: 10000,
        }
      );
      setWatchId(id);
      setTimeout(() => {
        navigator.geolocation.clearWatch(id);
        setWatchId(null);
      }, 35000);
    } catch {
      onFailure();
    }
  };

  const ipFallback = async () => {
    try {
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 7000);
      const res = await fetch("https://ipinfo.io/json", { signal: controller.signal });
      clearTimeout(t);
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data.loc === "string" && data.loc.includes(",")) {
          const [latStr, lngStr] = data.loc.split(",");
          const latitude = parseFloat(latStr);
          const longitude = parseFloat(lngStr);
          if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
            // Cache approximate IP-based location but do not mark permission allowed
            writeCache(latitude, longitude);
            logCoords(latitude, longitude);
            setOpen(false);
            setLoading(false);
            return;
          }
        }
      }
      const res2 = await fetch("https://ipwhois.app/json/");
      if (res2.ok) {
        const d2 = await res2.json();
        if (typeof d2.latitude === "number" && typeof d2.longitude === "number") {
          writeCache(d2.latitude, d2.longitude);
          logCoords(d2.latitude, d2.longitude);
          setOpen(false);
          setLoading(false);
          return;
        }
      }
      setError("Unable to fetch location. Please try again.");
      setOpen(!isSuppressed());
      setLoading(false);
    } catch {
      setError("Unable to fetch location. Please try again.");
      setOpen(!isSuppressed());
      setLoading(false);
    }
  };

  const handleDontAllow = () => {
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        <h2 className="text-xl font-semibold mb-2">Allow Location</h2>
        <p className="text-gray-600 mb-4">
          Enable location to fetch nearby results and improve your experience.
        </p>

        {error ? (
          <div className="text-red-600 text-sm mb-3">{error}</div>
        ) : null}

        <div className="flex gap-3">
          <button
            onClick={requestLocation}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white ${
              loading ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
            } transition-colors`}
          >
            {loading ? "Fetching..." : "Allow Location"}
          </button>
           <button
            onClick={handleDontAllow}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Don’t Allow
          </button> 
        </div>
      </div>
    </div>
  );
  */
  return null;
};

export default LocationTracker; 
